'use client'

import { format, parseISO } from 'date-fns'
import { Loader2, X } from 'lucide-react'
import { useMemo } from 'react'

import { useRedeemT } from '../i18n/useRedeemT'
import { formatDuration } from '../utils/timeSlots'
import { CTA_PRIMARY } from './styles'

interface Props {
  branchName?: string
  date?: string
  time?: string
  hours?: string
  guest?: number
  blocked: boolean
  isBooking: boolean
  locale: string

  onClose?: () => void
}

/**
 * Floating summary + CTA, bound to live form values, so it doubles as a review step.
 * Top-pinned below `md`, bottom-pinned above it: on a phone the bottom edge is where the keyboard
 * and browser chrome land, covering the bar exactly while the fields above are typed into.
 * Mobile nav is `h-14`, hence `top-16`.
 */
export default function StickySummaryBar({
  branchName,
  date,
  time,
  hours,
  guest,
  blocked,
  isBooking,
  locale,
  onClose
}: Props) {
  const t = useRedeemT()

  const summary = useMemo(() => {
    const parts: string[] = []

    if (branchName) parts.push(branchName)

    if (date) {
      const parsed = parseISO(date)
      parts.push(
        Number.isNaN(parsed.valueOf())
          ? date
          : new Intl.DateTimeFormat(locale, {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
          }).format(parsed)
      )
    }

    if (time) parts.push(time)
    // `hours` is H:MM, so `${hours} hrs` would read "2:00 hrs".
    if (hours) parts.push(formatDuration(hours))
    if (guest) parts.push(t('LABEL_GUEST_COUNT', { count: guest }))

    return parts.join(' · ')
  }, [branchName, date, time, hours, guest, locale, t])

  return (
    <div className='pointer-events-none fixed inset-x-0 top-16 z-40 md:bottom-6 md:top-auto'>
      <div className='container mx-auto max-w-4xl px-4'>
        <div className='pointer-events-auto flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-white/85 px-4 py-3 shadow-2xl backdrop-blur-md transition-colors duration-200 focus-within:bg-white hover:bg-white motion-reduce:transition-none md:gap-4 md:px-6 md:py-4'>
          <div className='min-w-0'>
            <div className='text-[11px] font-bold uppercase tracking-wider text-gray-500'>
              {t('LABEL_BOOKING_SUMMARY')}
            </div>
            <div className='truncate text-sm font-medium text-gray-800'>
              {summary || t('NOTICE_SUMMARY_INCOMPLETE')}
            </div>
          </div>

          <div className='flex shrink-0 items-center gap-1 md:gap-2'>
            <button
              type='submit'
              disabled={blocked || isBooking}
              className={`${CTA_PRIMARY} shrink-0 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:hover:scale-100 disabled:hover:bg-gray-300 disabled:hover:shadow-none`}>
              {isBooking ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' /> {t('BUTTON_BOOKING_LOADING')}
                </>
              ) : (
                t('BUTTON_SUBMIT')
              )}
            </button>

            {onClose && (
              /* `type='button'` is load-bearing: the bar sits inside the form, where a bare
                 button submits — an X that booked the appointment. */
              <button
                type='button'
                onClick={onClose}
                aria-label={t('BUTTON_HIDE_SUMMARY')}
                className='rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600'>
                <X className='h-4 w-4' />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
